import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { clientPortalFetch, getClientToken, setClientToken, clearClientToken } from './api';

const NAVY = '#0f1f45';

const BASE_NAV_ITEMS = [
  { path: '/client-portal/dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { path: '/client-portal/tasks', label: 'Tasks', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', settingKey: 'show_tasks' },
  { path: '/client-portal/documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', settingKey: 'show_documents' },
  { path: '/client-portal/ledger', label: 'Ledger', icon: 'M9 7h6m0 10v-3m-3 3v-6m-3 6v-1m-1-9h11a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h1', settingKey: 'show_ledger' },
  { path: '/client-portal/invoices', label: 'Invoices', icon: 'M9 14l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', settingKey: 'show_invoices' },
];
const REFERRAL_NAV_ITEM = { path: '/client-portal/referral', label: 'Referral', icon: 'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-2a4 4 0 10-4-4', settingKey: 'show_referral' };

function ClientPortalLayout({ children, title }) {
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!getClientToken()) { navigate('/client-portal/login'); return; }
    clientPortalFetch('/me').then(setMe).catch((e) => setError(e.message));
  }, [navigate]);

  const settings = me?.portal_settings || {};
  const visibleItems = BASE_NAV_ITEMS.filter((item) => settings[item.settingKey] !== false);
  const navItems = (me?.client?.is_referral_partner && settings.show_referral !== false)
    ? [...visibleItems, REFERRAL_NAV_ITEM]
    : visibleItems;

  const handleLogout = () => {
    clearClientToken();
    navigate('/client-portal/login');
  };

  // Same Google sign-in, another company: the server issues a fresh session for
  // the chosen company, so nobody has to log in again.
  const handleSwitchCompany = () => { setMenuOpen(false); setSwitchOpen(true); };
  const switchTo = async (clientId) => {
    if (clientId === me.client.id) { setSwitchOpen(false); return; }
    setSwitchingId(clientId);
    try {
      const r = await clientPortalFetch('/switch-company', { method: 'POST', body: JSON.stringify({ client_id: clientId }) });
      setClientToken(r.token);
      window.location.reload();
    } catch (e) { setError(e.message); setSwitchingId(null); setSwitchOpen(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex' }}>
      {/* ── Desktop sidebar ── */}
      <aside className="cp-sidebar" style={{
        width: 240, background: NAVY, color: '#fff', flexShrink: 0,
        display: 'flex', flexDirection: 'column', padding: '24px 0',
      }}>
        <div style={{ padding: '0 20px 24px', fontSize: 18, fontWeight: 700, letterSpacing: 0.3 }}>
          Client Portal
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px',
                color: active ? '#fff' : '#9ca3af', textDecoration: 'none', fontSize: 14,
                fontWeight: active ? 600 : 500,
                background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                borderLeft: active ? '3px solid #f97316' : '3px solid transparent',
              }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div style={{ padding: '16px 20px 0', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 16 }}>
          {me?.companies?.length > 1 && (
            <button onClick={handleSwitchCompany} style={sidebarFooterBtn}>Switch Company</button>
          )}
          <button onClick={handleLogout} style={{ ...sidebarFooterBtn, color: '#f87171' }}>Logout</button>
        </div>
      </aside>

      {/* ── Mobile top bar ── */}
      <div className="cp-mobile-topbar" style={{
        display: 'none', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 30,
        background: NAVY, color: '#fff', padding: '14px 16px', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 700, fontSize: 16 }}>{title || 'Client Portal'}</span>
        <button onClick={() => setMenuOpen((v) => !v)} style={{ background: 'none', border: 'none', color: '#fff', padding: 4 }}>
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      {menuOpen && (
        <div className="cp-mobile-menu" style={{
          display: 'none', position: 'fixed', top: 52, left: 0, right: 0, zIndex: 30,
          background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 8,
        }}>
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} onClick={() => setMenuOpen(false)} style={{
              display: 'block', padding: '12px 14px', color: NAVY, textDecoration: 'none',
              fontSize: 14, fontWeight: 500, borderRadius: 8,
              background: location.pathname === item.path ? '#f1f5f9' : 'transparent',
            }}>
              {item.label}
            </Link>
          ))}
          {me?.companies?.length > 1 && (
            <button onClick={handleSwitchCompany} style={mobileMenuBtn}>Switch Company</button>
          )}
          <button onClick={handleLogout} style={{ ...mobileMenuBtn, color: '#dc2626' }}>Logout</button>
        </div>
      )}

      {/* ── Bottom nav (phones) ── */}
      <nav className="cp-bottom-nav" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
        background: '#fff', borderTop: '1px solid #e5e7eb', padding: '6px 0',
        justifyContent: 'space-around',
      }}>
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              color: active ? NAVY : '#9ca3af', textDecoration: 'none', fontSize: 11, padding: 6,
            }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* ── Main content ── */}
      <main className="cp-main" style={{ flex: 1, padding: '28px 32px', minWidth: 0 }}>
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {me && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 24, flexWrap: 'wrap', gap: 12,
          }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: NAVY, margin: 0 }}>
                {title || me.client.client_name}
              </h1>
              <p style={{ color: '#9ca3af', fontSize: 13, margin: '4px 0 0' }}>
                {me.client.client_name} · {me.client.firm_name}
                {!me.client.is_active && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#b91c1c', background: '#fee2e2', padding: '2px 8px', borderRadius: 999 }}>
                    Inactive
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {me ? React.cloneElement(children, { me }) : !error && <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</div>}
      </main>

      {switchOpen && me && (
        <div onClick={() => setSwitchOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420, padding: 20, boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Switch company</div>
            <div style={{ fontSize: 12.5, color: '#9ca3af', marginBottom: 14 }}>No need to sign in again.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
              {me.companies.map((c) => {
                const current = c.client_id === me.client.id;
                return (
                  <button key={c.client_id} onClick={() => switchTo(c.client_id)} disabled={switchingId !== null} style={{
                    textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                    border: current ? '2px solid #f97316' : '1px solid #e5e7eb', background: current ? '#fff7ed' : '#fff',
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{c.client_name}</div>
                    <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 2 }}>
                      {current ? 'Currently viewing' : switchingId === c.client_id ? 'Switching…' : (c.group_name || c.firm_name)}
                      {!c.is_active && ' · Inactive'}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .cp-sidebar { display: none !important; }
          .cp-mobile-topbar { display: flex !important; }
          .cp-bottom-nav { display: flex !important; }
          .cp-main { padding: 84px 16px 84px !important; }
        }
      `}</style>
    </div>
  );
}

const sidebarFooterBtn = {
  display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
  color: '#9ca3af', fontSize: 13, padding: '10px 0', cursor: 'pointer',
};

const mobileMenuBtn = {
  display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
  color: '#6b7280', fontSize: 13, padding: '10px 14px', cursor: 'pointer',
};

export default ClientPortalLayout;
